<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'FieldsetContractService',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    item: {
      type: Object,
      required: true,
    },
    labelAlign: {
      type: String,
      required: false,
      default: 'right',
    },
    readonly: {
      type: Boolean,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const billingCategories = ref(null);
  const services = ref(null);
  const invoiceGroups = ref(null);
  const ux = ref([]);
  const b_3943151158818434_multiple = computed(() => false);

  const $data = computed(() => ({
    billingCategories: billingCategories.value,
    services: services.value,
    invoiceGroups: invoiceGroups.value,
    ux: ux.value,
    b_3943151158818434_multiple: b_3943151158818434_multiple.value,
  }));

  const $$load_billingCategories = () => {
    return $fn.value.fetch('get_tags', {
      parameters: { tag_type: 'billing_category' },
    });
  };
  const $$load_services = () => {
    return $fn.value
      .fetch('suggest_product', {
        parameters: {
          query: '*',
          top: 50,
          skip: 0,
          product_types: 'service',
          product_invoice_groups: 'all',
        },
      })
      .then((data) => _.orderBy(data, [(p) => p.name.toLowerCase()]));
  };
  const $$load_invoiceGroups = () => {
    return $fn.value.fetch('list_product_invoice_group');
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'billingCategories' },
      { name: 'services' },
      { name: 'invoiceGroups' },
      { name: 'ux' },
      { name: 'b_3943151158818434_multiple', nodeId: '3943151158818434' },
    ],
    loaders: {
      billingCategories: $$load_billingCategories,
      services: $$load_services,
      invoiceGroups: $$load_invoiceGroups,
    },
    ux: {},
    dataRefs: {
      billingCategories,
      services,
      invoiceGroups,
      ux,
      b_3943151158818434_multiple,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.item,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('item');
    }
  );
  watch(
    () => props.labelAlign,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('labelAlign');
    }
  );
  watch(
    () => props.readonly,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('readonly');
    }
  );

  const act_6466705138206980_setData_ywepa2fw3_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'item.name',
      value: $event.data.option.name,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_6466705138206980_setData_y262xs86e_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'item.service_id',
      value: $event.data.option.id,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_6466705138206980_setData_nym1h7n3d_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'feeItem.unit',
      value: $event.data.option.unit,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };

  const act_7315092382398562_reloadComponentData_dmxgbk54j =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_z5zaz0vb5 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_dmxgbk54j,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_z5zaz0vb5],
    event: 'reload',
  });
  const act_6466705138206980_setData_ywepa2fw3 = new actionTypes.SetDataAction({
    actionArgs: act_6466705138206980_setData_ywepa2fw3_getActionArgs,
  });
  const inst_evh_6466705138206980_optionSelected_cqfiuohmz =
    new actionTypes.EventHandlerImpl({
      action: act_6466705138206980_setData_ywepa2fw3,
      event: 'optionSelected',
      displayName: 'setData',
    });
  const act_6466705138206980_setData_y262xs86e = new actionTypes.SetDataAction({
    actionArgs: act_6466705138206980_setData_y262xs86e_getActionArgs,
  });
  const inst_evh_6466705138206980_optionSelected_0tvp0a0db =
    new actionTypes.EventHandlerImpl({
      action: act_6466705138206980_setData_y262xs86e,
      event: 'optionSelected',
      displayName: 'setData',
    });
  const act_6466705138206980_setData_nym1h7n3d = new actionTypes.SetDataAction({
    actionArgs: act_6466705138206980_setData_nym1h7n3d_getActionArgs,
  });
  const inst_evh_6466705138206980_optionSelected_irv8mh90s =
    new actionTypes.EventHandlerImpl({
      action: act_6466705138206980_setData_nym1h7n3d,
      event: 'optionSelected',
      displayName: 'setData',
    });
  const inst_evh_6466705138206980_optionSelected =
    new actionTypes.EventHandlerGroup({
      handlers: [
        inst_evh_6466705138206980_optionSelected_cqfiuohmz,
        inst_evh_6466705138206980_optionSelected_0tvp0a0db,
        inst_evh_6466705138206980_optionSelected_irv8mh90s,
      ],
      event: 'optionSelected',
    });

  const actions = {
    inst_evh_7315092382398562_reload_z5zaz0vb5,
    inst_evh_7315092382398562_reload,
    inst_evh_6466705138206980_optionSelected_cqfiuohmz,
    inst_evh_6466705138206980_optionSelected_0tvp0a0db,
    inst_evh_6466705138206980_optionSelected_irv8mh90s,
    inst_evh_6466705138206980_optionSelected,
  };

  const evh_7315092382398562_reload_z5zaz0vb5 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_z5zaz0vb5.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_optionSelected_cqfiuohmz = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_6466705138206980_optionSelected_cqfiuohmz.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_optionSelected_0tvp0a0db = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_6466705138206980_optionSelected_0tvp0a0db.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_optionSelected_irv8mh90s = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_6466705138206980_optionSelected_irv8mh90s.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_optionSelected = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_optionSelected.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <fieldset>
      <ItpFormGroup
        name="service"
        :label="pgettext('ContractService', 'Service')"
        :label-cols-md="3"
        :content-cols-md="9"
        :labelAlign="labelAlign"
        :helpTooltip="
          pgettext(
            'ContractService|Tooltip',
            'Select the product or service the invoice line refers to.'
          )
        "
        required
      >
        <ItpFormSearchableSelect
          name="client"
          v-model="item.service"
          bind-object
          :options="services"
          textField="name"
          :readonly="readonly"
          @optionSelected="evh_6466705138206980_optionSelected($event, {})"
        >
        </ItpFormSearchableSelect>
      </ItpFormGroup>
      <ItpFormGroup
        :label-cols-md="3"
        :content-cols-md="9"
        name="invoice_group_id"
        :label="pgettext('ContractService', 'Invoice Group')"
        :labelAlign="labelAlign"
        :helpTooltip="
          pgettext(
            'ContractService|Tooltip',
            'The invoice group this service belongs to.'
          )
        "
      >
        <ItpFormSearchableSelect
          name="invoice_group_id"
          v-model="item.invoice_group_id"
          :multiple="b_3943151158818434_multiple"
          :options="invoiceGroups"
          valueField="id"
          textField="name"
          clearable
          :readonly="readonly"
        >
        </ItpFormSearchableSelect>
      </ItpFormGroup>
      <ItpFormGroup
        :label-cols-md="3"
        name="intermediated_service"
        :label="pgettext('ContractService', 'Intermediated service')"
        :labelAlign="labelAlign"
        :helpTooltip="
          pgettext(
            'ContractService|Tooltip',
            'Select this option if the service is provided via an intermediary party.'
          )
        "
      >
        <ItpFormCheckbox
          name="intermediated_service"
          v-model="item.intermediated_service"
          :readonly="readonly"
        >
        </ItpFormCheckbox>
      </ItpFormGroup>
      <ItpFormGroup
        name="valid_from"
        :label="pgettext('ContractService', 'Valid from')"
        :label-cols-md="3"
        :content-cols-md="4"
        :labelAlign="labelAlign"
        :helpTooltip="
          pgettext(
            'ContractService|Tooltip',
            'Specifies the start date of the service period.'
          )
        "
      >
        <ItpFormDatetime
          name="valid_from"
          v-model="item.valid_from"
          required
          :readonly="readonly"
        >
        </ItpFormDatetime>
      </ItpFormGroup>
      <ItpFormGroup
        name="valid_to"
        :label="pgettext('ContractService', 'Valid to')"
        :label-cols-md="3"
        :content-cols-md="4"
        :labelAlign="labelAlign"
        :helpTooltip="
          pgettext(
            'ContractService|Tooltip',
            'The service end date. Set only if it\'s different from the contract\'s end date.'
          )
        "
      >
        <ItpFormDatetime
          name="valid_to"
          v-model="item.valid_to"
          :readonly="readonly"
        >
        </ItpFormDatetime>
      </ItpFormGroup>
      <ItpFormGroup
        name="valid_to"
        :label="pgettext('ContractService', 'Description')"
        :label-cols-md="3"
        :content-cols-md="9"
        :labelAlign="labelAlign"
        :helpTooltip="
          pgettext(
            'ContractService|Tooltip',
            'Internal notes for reference only. Not visible externally.'
          )
        "
      >
        <ItpFormTextarea
          name="description"
          v-model="item.description"
          :readonly="readonly"
          :rows="5"
        >
        </ItpFormTextarea>
      </ItpFormGroup>
    </fieldset>
    <hr />
    <fieldset>
      <legend>
        {{ pgettext('ContractServiceEditor|Section', 'Invoicing') }}
      </legend>
      <ItpFormGroup
        :label-cols-md="3"
        :content-cols-md="6"
        name="billing_category"
        :label="pgettext('ContractService', 'Billing category')"
        :labelAlign="labelAlign"
        :helpTooltip="
          pgettext(
            'ContractService|Help|BillingCategory',
            'The invoice category this service should be assigned to when billed. New values entered will be saved for future use.'
          )
        "
      >
        <ItpFormSearchableSelect
          name="billing_category"
          taggable
          :options="billingCategories"
          v-model="item.billing_category"
          :readonly="item.readonly"
          valueField="name"
          textField="name"
        >
        </ItpFormSearchableSelect>
      </ItpFormGroup>
      <ItpFormGroup
        name="name"
        :label="pgettext('ContractService', 'Name')"
        :label-cols-md="3"
        :content-cols-md="9"
        :labelAlign="labelAlign"
        :helpTooltip="
          pgettext(
            'ContractService|Tooltip',
            'This text will appear on the invice line.'
          )
        "
        required
      >
        <ItpFormInput
          name="name"
          v-model="item.name"
          required
          :readonly="readonly"
        >
        </ItpFormInput>
      </ItpFormGroup>
      <ItpFormGroup
        name="name"
        :label="pgettext('ContractService', 'Name(s)')"
        :label-cols-md="3"
        :content-cols-md="9"
        :labelAlign="labelAlign"
        :helpTooltip="
          pgettext(
            'ContractService|Tooltip',
            'This text will appear on the invice line.'
          )
        "
        required
      >
        <UCContractServiceItemNameEditor
          v-model="item.invoice_item_name"
          :readonly="readonly"
        >
        </UCContractServiceItemNameEditor>
      </ItpFormGroup>
      <ItpFormGroup :label-cols-md="3" :content-cols-md="9">
        <hr class="dots" />
      </ItpFormGroup>
      <ItpFormGroup
        name="invoice_notes"
        :label="pgettext('ContractServiceFee', 'Invoice notes')"
        :label-cols-md="3"
        :content-cols-md="9"
        :labelAlign="labelAlign"
        :helpTooltip="
          pgettext(
            'ContractService|Tooltip',
            'Note to display on the invoice line.'
          )
        "
      >
        <ItpFormTextarea
          name="fee_notes"
          v-model="item.invoice_notes"
          :readonly="readonly"
        >
        </ItpFormTextarea>
      </ItpFormGroup>
      <ItpFormGroup
        name="billing_frequency"
        :label="pgettext('ContractService', 'Billing frequency')"
        :label-cols-md="3"
        :content-cols-md="3"
        :labelAlign="labelAlign"
        :helpTooltip="
          pgettext(
            'ContractService|Tooltip',
            'The cycle length of issuing the invoices.'
          )
        "
      >
        <BillingFrequencySelector
          v-model="item.billing_frequency"
          :readonly="readonly"
        >
        </BillingFrequencySelector>
      </ItpFormGroup>
      <ItpFormGroup
        name="billing_date_rule"
        :label="pgettext('ContractService', 'Billing date rule')"
        :label-cols-md="3"
        :content-cols-md="9"
        :labelAlign="labelAlign"
        :helpTooltip="
          pgettext(
            'ContractService|Tooltip',
            'The cycle length of issuing the invoices.'
          )
        "
      >
        <BillingDateRuleFields
          v-model="item.billing_date_rule"
          :readonly="readonly"
        >
        </BillingDateRuleFields>
      </ItpFormGroup>
      <ItpFormGroup :label-cols-md="3" :content-cols-md="9">
        <hr class="dots" />
      </ItpFormGroup>
      <ItpFormGroup
        name="due_date_rule"
        :label="pgettext('ContractService', 'Due date rule')"
        :label-cols-md="3"
        :content-cols-md="9"
        :labelAlign="labelAlign"
        :helpTooltip="
          pgettext(
            'ContractService|Tooltip',
            'The rule of calculating the payment dates.'
          )
        "
      >
        <DueDateRuleFields v-model="item.due_date_rule" :readonly="readonly">
        </DueDateRuleFields>
      </ItpFormGroup>
      <ItpFormGroup :label-cols-md="3" :content-cols-md="9">
        <hr class="dots" />
      </ItpFormGroup>
      <ItpFormGroup
        name="billing_currency"
        :label="pgettext('ContractService', 'Billing currency')"
        :label-cols-md="3"
        :content-cols-md="3"
        :labelAlign="labelAlign"
        :helpTooltip="
          pgettext(
            'ContractService|Tooltip',
            'The currency to use on the invoice.'
          )
        "
      >
        <BillingCurrencySelector
          v-model="item.billing_currency"
          :readonly="readonly"
        >
        </BillingCurrencySelector>
      </ItpFormGroup>
      <ItpFormGroup
        name="exchange_rate_source"
        :label="pgettext('ContractService', 'Exchange rate source')"
        :label-cols-md="3"
        :content-cols-md="3"
        :labelAlign="labelAlign"
        :helpTooltip="
          pgettext(
            'ContractService|Tooltip',
            'The currency exchange rate\'s provider.'
          )
        "
      >
        <ExchangeRateSourceSelector
          v-model="item.exchange_rate_source"
          :readonly="readonly"
        >
        </ExchangeRateSourceSelector>
      </ItpFormGroup>
      <ItpFormGroup
        name="exchange_rate_source_spec"
        :label="
          pgettext('ContractService', 'Exchange rate source specification')
        "
        :label-cols-md="3"
        :content-cols-md="9"
        :labelAlign="labelAlign"
        :helpTooltip="
          pgettext(
            'ContractService|Tooltip',
            'The currency exchange rate configuration.'
          )
        "
      >
        <ExchangeRateSourceSpecEditor
          v-model="item.exchange_rate_source_spec"
          :readonly="readonly"
        >
        </ExchangeRateSourceSpecEditor>
      </ItpFormGroup>
      <ItpFormGroup
        name="exchange_rate_date_rule"
        :label="pgettext('ContractService', 'Exchange rate date rule')"
        :label-cols-md="3"
        :content-cols-md="9"
        :labelAlign="labelAlign"
        :helpTooltip="
          pgettext(
            'ContractService|Tooltip',
            'The date used to determine the exchange rate for billing purposes.'
          )
        "
      >
        <ExchangeRateDateRuleFields
          v-model="item.exchange_rate_date_rule"
          :readonly="readonly"
        >
        </ExchangeRateDateRuleFields>
      </ItpFormGroup>
    </fieldset>
  </ItpUsercontrol>
</template>
